import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  leadTimeProfileSchema,
  AIWorkflow,
  AR_AGING_BASES,
  AR_RECOMMENDATION_SPEED_PREFERENCES,
  AR_STATEMENT_RECIPIENT_ROLE_MODES,
  EMAIL_PROVIDER,
  MAX_THRESHOLD_BUFFER_PCT,
  ModuleConfig,
  ProductModule,
} from "@erp/shared-types";
import { CollectionActionDefinitionSchema } from "../../accounts-receivable/ar-customer-intelligence.contract";

// Validation schemas
const productModuleSchema = z.enum(ProductModule);

/**
 * Every key `ModuleConfig["shipAndDebit"]` declares, mirrored here.
 *
 * Field docs live on the TS type in `packages/shared-types/src/ProductModules.ts`
 * — this is the runtime half, and `shipAndDebitKeyParity` below keeps the two
 * from drifting apart.
 */
const shipAndDebitShape = z.object({
  sd_844_email_provider: z
    .enum([EMAIL_PROVIDER.SENDGRID, EMAIL_PROVIDER.MICROSOFT_GRAPH])
    .optional(),
  sd_844_email_mailbox_upn: z.string().optional(),
  sd_844_error_notification_emails: z.array(z.string()).optional(),
  /** Clamped server-side by `resolveReviewQueueCap`, so no bounds here. */
  sd_849_review_queue_cap: z.number().optional(),
  /** Clamped server-side by `resolveAuditSampleRate`, so no bounds here. */
  sd_849_audit_sample_rate: z.number().optional(),
  sd_849_group_by_claim: z.boolean().optional(),
  sd_gl_je_template: z
    .object({
      trans_type: z.string().optional(),
      user_id: z.string().optional(),
      user_email: z.string().optional(),
      user_email_by_division: z.record(z.string(), z.string()).optional(),
      source_code: z.string().optional(),
    })
    .passthrough()
    .optional(),
  sd_vdm_no_tax_province_by_division: z
    .record(z.string(), z.string())
    .optional(),
});

/**
 * Compile-time drift guard. A key added to `ModuleConfig["shipAndDebit"]` but
 * forgotten here fails `pnpm typecheck` with the missing key as the error text,
 * instead of being silently stripped off every read and write.
 *
 * It has to be asserted against the STRICT shape: `.passthrough()` adds an index
 * signature, which makes `keyof` match everything and the check vacuous.
 *
 * `moduleConfigSchema` being annotated `z.ZodType<ModuleConfig>` does NOT cover
 * this — every field is optional, so a schema missing a key still satisfies the
 * annotation.
 */
type ShipAndDebitKeysMissingFromZod = Exclude<
  keyof NonNullable<ModuleConfig["shipAndDebit"]>,
  keyof z.infer<typeof shipAndDebitShape>
>;
const _shipAndDebitKeyParity: ShipAndDebitKeysMissingFromZod extends never
  ? true
  : ShipAndDebitKeysMissingFromZod = true;
void _shipAndDebitKeyParity;

/**
 * `.passthrough()` is load-bearing, not decoration: a nested `z.object` STRIPS
 * unknown keys, on the response as well as the request, and the handler replaces
 * `config` wholesale — so a key this schema does not know about disappears from
 * the Modules settings page and is deleted the moment someone saves there. The
 * parity guard above should keep that list empty, but passthrough means a key
 * that lands in the DB ahead of the schema (via `patchModuleConfigSection`)
 * survives rather than being destroyed.
 *
 * Keep it ahead of `.refine()` — `.refine()` returns a wrapper that cannot be
 * made loose afterwards.
 */
const shipAndDebitSchema = shipAndDebitShape
  .passthrough()
  .refine(
    (val) =>
      val.sd_844_email_provider !== EMAIL_PROVIDER.MICROSOFT_GRAPH ||
      !!val.sd_844_email_mailbox_upn,
    "sd_844_email_mailbox_upn is required when sd_844_email_provider is microsoft_graph",
  );

/**
 * Every settings-bearing object in here is `.passthrough()`, at each level of
 * nesting, and that is a deliberate policy rather than a per-section accident.
 *
 * A plain `z.object` STRIPS unknown keys in BOTH directions. This schema is the
 * response type as well as the request type of the Modules settings page, so a
 * key it does not name is erased on the way out — the JSON editor renders a
 * config with the key missing, and it reads as unset even when the row holds it.
 * Settings reach this JSONB from writers that do not go through the page at all
 * (`patchModuleConfigSection`, `sd849FlagStore`), so "the schema knows every
 * key" is not something this file can assume; it only knows the keys that were
 * remembered. `shipAndDebit` lost its flags this way, and `ar` lost fields
 * before that.
 *
 * `mergeModuleConfigEdit` already stops a stripped key from being DELETED on
 * save (a key absent from `base` is never treated as a deletion), so what
 * passthrough buys on top of that is visibility: the key survives the round trip
 * and can actually be seen and edited.
 *
 * Two rules when adding to this schema:
 * - Put `.passthrough()` BEFORE any `.refine()`. `.refine()` returns a wrapper
 *   that cannot be made loose afterwards, so the order is load-bearing.
 * - Objects that describe a RECORD rather than a settings bag — array elements
 *   like `approvalThresholds`, `remediationClasses`, `waitingOnOptions`,
 *   `registeredEmailDomains` — stay strict. There is no out-of-band writer
 *   adding fields to those, and strictness is worth more than tolerance.
 */
/**
 * Replenishment auto-approve rules (GEN-2936). Validated on write here, and the
 * engine re-parses on read so a malformed stored value means "off", never a
 * crash or a looser rule.
 */
export const procurementAutoApproveConfigSchema = z
  .object({
    enabled: z.boolean().optional(),
    popularityCodes: z.array(z.string().trim().min(1)).min(1).optional(),
    movementMonths: z.number().int().min(1).max(36).optional(),
    minMovingMonths: z.number().int().min(1).max(36).optional(),
    minStockedLocations: z.number().int().min(1).optional(),
  })
  .passthrough();

/**
 * Product keys the replenishment engine never plans (GEN-2938). Validated on
 * write; the engine re-parses on read and treats a malformed value as empty.
 */
export const procurementNonItemSkusSchema = z
  .array(z.string().trim().min(1).max(255))
  .max(1000);

export const moduleConfigSchema: z.ZodType<ModuleConfig> = z
  .object({
    procurement: z
      .object({
        leadTimeProfile: leadTimeProfileSchema.nullable().optional(),
        autoApprove: procurementAutoApproveConfigSchema.optional(),
        thresholdBufferPct: z
          .number()
          .int()
          .min(0)
          .max(MAX_THRESHOLD_BUFFER_PCT)
          .optional(),
        nonItemSkus: procurementNonItemSkusSchema.optional(),
      })
      .passthrough()
      .optional(),
    emailWorkflows: z
      .partialRecord(
        z.enum(AIWorkflow),
        z.object({
          registeredEmailDomains: z.array(
            z.object({
              header: z.enum(["to", "cc"]),
              domain: z.string(),
            }),
          ),
        }),
      )
      .optional(),
    rebates: z
      .object({
        reportedAlgorithm: z
          .enum(["linear-regression", "conservative"])
          .optional(),
        conservativeRiskFactor: z.number().min(0).max(1).optional(),
        seasonalDampeningFactor: z.number().min(0).max(1).optional(),
        enabledStrategies: z
          .array(z.enum(["linear-regression", "conservative"]))
          .optional(),
        perYear: z
          .record(
            z.string().regex(/^\d{4}$/),
            z
              .object({
                reportedAlgorithm: z
                  .enum(["linear-regression", "conservative"])
                  .optional(),
                conservativeRiskFactor: z.number().min(0).max(1).optional(),
                seasonalDampeningFactor: z.number().min(0).max(1).optional(),
                enabledStrategies: z
                  .array(z.enum(["linear-regression", "conservative"]))
                  .optional(),
              })
              .passthrough(),
          )
          .optional(),
        followupMailboxUpn: z.string().optional(),
        followupTemplateId: z.string().uuid().optional(),
        followupContactTagOptionId: z.string().uuid().optional(),
        followupCadenceDays: z.number().optional(),
      })
      .passthrough()
      .optional(),
    ap: z
      .object({
        invoiceLineItemPurchaseMatching: z
          .array(z.array(z.string()))
          .optional(),
        disableRemittanceEmail: z.boolean().optional(),
        remittanceMailboxUpn: z.string().email().optional(),
        remittanceSenderAddress: z.string().email().optional(),
        remittanceContactEmails: z.array(z.string().email()).optional(),
        remittanceContactPhone: z.string().optional(),
        paymentScheduling: z
          .object({
            mode: z.enum(["immediate", "payRun", "latestDate"]),
            cron: z.string().optional(),
            immediacy: z.enum(["asap", "latest"]).optional(),
            rounding: z.enum(["up", "down", "nearest"]).optional(),
            timezone: z.string().optional(),
            daysBeforeDue: z.number().positive().optional(),
            paymentTermsPriority: z
              .array(z.enum(["invoice", "supplier", "company"]))
              .refine(
                (sources) => new Set(sources).size === sources.length,
                "paymentTermsPriority must not contain duplicate sources",
              )
              .optional(),
          })
          .passthrough()
          .optional(),
        paymentTermsPriority: z
          .array(z.enum(["invoice", "supplier", "company"]))
          .refine(
            (sources) => new Set(sources).size === sources.length,
            "paymentTermsPriority must not contain duplicate sources",
          )
          .optional(),
        defaultOutgoingPaymentTime: z
          .string()
          .regex(
            /^([01]\d|2[0-3]):[0-5]\d$/,
            "Must be in HH:mm 24-hour format (e.g. 09:00, 14:30)",
          )
          .optional(),
        approvalThresholds: z
          .array(
            z.object({
              minAmount: z.number(),
              requiredApprovals: z.number(),
            }),
          )
          .optional(),
      })
      .passthrough()
      .refine(
        (value) =>
          !value.remittanceSenderAddress || !!value.remittanceMailboxUpn,
        {
          message: "remittanceSenderAddress requires remittanceMailboxUpn",
          path: ["remittanceSenderAddress"],
        },
      )
      .optional(),
    ar: z
      .object({
        outboxMailboxUpn: z.string().email().optional(),
        outboxSenderAddress: z.string().email().optional(),
        outboxBccEmails: z.array(z.string().email()).optional(),
        statementRecipientRoles: z
          .enum(AR_STATEMENT_RECIPIENT_ROLE_MODES)
          .optional(),
        outboxContactEmails: z.array(z.string().email()).optional(),
        outboxContactPhone: z.string().optional(),
        statementContactFooterEnabled: z.boolean().optional(),
        logoUrl: z.string().url().optional(),
        defaultPdfFilenamePrefix: z.string().max(64).optional(),
        internalOnlyEnabled: z.boolean().optional(),
        internalOnlyDomains: z.array(z.string().min(1)).optional(),
        statementDefaultBasis: z.enum(AR_AGING_BASES).optional(),
        lockStatementBasis: z.boolean().optional(),
        cashReceiptTrackingStartDate: z.string().date().optional(),
        collections: z
          .object({
            internalOnlyEnabled: z.boolean().optional(),
            autoDetectShortPaysEnabled: z.boolean().optional(),
            shortPayMinimumAmount: z
              .string()
              .regex(/^\d+(\.\d+)?$/, {
                message: "Must be a non-negative decimal string",
              })
              .optional(),
            shortPayGraceDays: z.number().int().min(0).optional(),
            autoDetectOverPaymentsEnabled: z.boolean().optional(),
            overPaymentMinimumAmount: z
              .string()
              .regex(/^\d+(\.\d+)?$/, {
                message: "Must be a non-negative decimal string",
              })
              .optional(),
            autoDetectDeductionsEnabled: z.boolean().optional(),
            deductionMinimumAmount: z
              .string()
              .regex(/^\d+(\.\d+)?$/, {
                message: "Must be a non-negative decimal string",
              })
              .optional(),
            defaultAssigneeUserId: z.string().uuid().optional(),
            recommendations: z
              .object({
                speedPreference: z
                  .enum(AR_RECOMMENDATION_SPEED_PREFERENCES)
                  .optional(),
              })
              .passthrough()
              .optional(),
            interpretDiscrepanciesEnabled: z.boolean().optional(),
            remediationClasses: z
              .array(
                z.object({
                  key: z.string().min(1).max(64),
                  label: z.string().min(1).max(120),
                  guidance: z.string().max(500).optional(),
                }),
              )
              .max(50)
              .optional(),
            recommendationActions: z
              .array(CollectionActionDefinitionSchema)
              .max(50)
              .refine(
                (actions) =>
                  new Set(actions.map((a) => a.id)).size === actions.length,
                { message: "Recommendation action ids must be unique." },
              )
              .optional(),
            waitingOnOptions: z
              .array(
                z.object({
                  key: z.string().min(1).max(64),
                  label: z.string().min(1).max(120),
                  defaultFollowUpDays: z
                    .number()
                    .int()
                    .min(0)
                    .max(365)
                    .optional(),
                  enabled: z.boolean(),
                }),
              )
              .max(25)
              .refine(
                (options) =>
                  new Set(options.map((o) => o.key)).size === options.length,
                { message: "Waiting-on option keys must be unique." },
              )
              .optional(),
            agentInstructions: z.string().max(4000).optional(),
            // Longer cap than agentInstructions: this REPLACES a ~2k-char
            // default the editor seeds it with, so an org tuning it for their
            // portal starts near the limit rather than at zero.
            portalBackupExtractionInstructions: z.string().max(8000).optional(),
          })
          .passthrough()
          .optional(),
        emailIntelligence: z
          .object({
            consentedMailboxes: z.array(z.string()).optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough()
      .refine(
        (value) => !value.outboxSenderAddress || !!value.outboxMailboxUpn,
        {
          message: "outboxSenderAddress requires outboxMailboxUpn",
          path: ["outboxSenderAddress"],
        },
      )
      .refine(
        (value) =>
          !value.internalOnlyEnabled ||
          (value.internalOnlyDomains?.length ?? 0) > 0,
        {
          message: "internalOnlyEnabled requires at least one internal domain",
          path: ["internalOnlyDomains"],
        },
      )
      .refine(
        (value) => !value.lockStatementBasis || value.statementDefaultBasis,
        {
          message: "lockStatementBasis requires statementDefaultBasis",
          path: ["statementDefaultBasis"],
        },
      )
      .optional(),
    orderManagement: z
      .object({
        enabledCustomerIds: z.array(z.string()).optional(),
        disabledCustomerIds: z.array(z.string()).optional(),
        requestDateStrategy: z
          .enum(["inventory-lead-time", "shipping-method"])
          .optional(),
        autoSubmitEnabled: z.boolean().optional(),
        manualApprovalEnabled: z.boolean().optional(),
        skipDescriptionBasedProductMatching: z.boolean().optional(),
        legacyDraftAssignmentEmailsDisabled: z.boolean().optional(),
        mergeSplitPurchaseOrdersEnabled: z.boolean().optional(),
        enhancedSalesOrderUi: z.boolean().optional(),
        defaultPriceListId: z.string().uuid().optional(),
        standardPaymentTermsDays: z.number().int().min(0).optional(),
        standardShippingTerms: z.string().max(64).optional(),
        defaultSalesCredit: z
          .object({
            salespersonId: z.string().min(1).max(64),
            name: z.string().min(1).max(200),
          })
          .optional(),
        costCurrency: z.enum(["USD", "EUR"]).optional(),
      })
      .passthrough()
      .optional(),
    shipAndDebit: shipAndDebitSchema.optional(),
    oracleEbs: z
      .object({
        queryServerUrl: z.string().optional(),
        defaultOrgCode: z.string().optional(),
        defaultOrderType: z
          .object({ id: z.number(), name: z.string() })
          .optional(),
        defaultPriceListName: z.string().optional(),
        defaultInventoryOrgId: z.number().optional(),
        ebsOrgName: z.string().optional(),
        fobPoint: z.string().optional(),
        promoCode: z.string().optional(),
        salesChannelCode: z.string().optional(),
      })
      .passthrough()
      .optional(),
    oracleFusion: z
      .object({
        businessUnitId: z.number().optional(),
        transactionTypeCode: z.string().optional(),
        fulfillmentOrganizationId: z.number().optional(),
        paymentTermsCode: z.number().optional(),
        freightTermsCode: z.string().optional(),
        defaultCurrencyCode: z.string().optional(),
        sourceTransactionSystem: z.string().optional(),
        defaultLineTypeCode: z.string().optional(),
      })
      .passthrough()
      .optional(),
    mappings: z
      .record(z.string(), z.record(z.string(), z.unknown()))
      .optional(),
    mappingsMeta: z
      .record(
        z.string(),
        z.object({
          displayName: z.string().optional(),
          keyLabel: z.string().nullable().optional(),
          columnOrder: z.array(z.string()).optional(),
        }),
      )
      .optional(),
  })
  .passthrough();

const organizationModuleConfigSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  module: productModuleSchema,
  enabled: z.boolean(),
  config: moduleConfigSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});

/**
 * Input uses moduleConfigSchema with .passthrough() so known fields are validated
 * (e.g., shipAndDebit.sd_844_email_provider must be "sendgrid" | "microsoft_graph")
 * while unrecognized keys are preserved. Consumers should read fields defensively
 * (e.g., config?.shipAndDebit?.sd_844_email_provider) and apply defaults at read time.
 */
const createOrUpdateOrganizationModuleConfigInputSchema = z.object({
  module: productModuleSchema,
  enabled: z.boolean().optional(),
  config: moduleConfigSchema,
  /**
   * The config this edit was made against. **Required** — it is what makes the
   * save a reconcile instead of a replace.
   *
   * Every caller builds `config` by spreading the copy it fetched when the page
   * loaded. Writing that straight back overwrites anything committed in
   * between, and the loser of the race gets no error: this is how a JE template
   * saved from the Company Locations page disappeared when someone saved an
   * unrelated card on the Modules page from a stale snapshot.
   *
   * With `baseConfig` the server can tell a key the user deliberately removed
   * (in `base`, absent from `edited`) from one they simply never saw (absent
   * from `base`, present in `current`), and reconciles under a row lock.
   *
   * It is deliberately NOT optional: making it so is what stops the next caller
   * from reintroducing the replace by leaving it out. A caller with nothing to
   * reconcile against sends `{}`.
   */
  baseConfig: moduleConfigSchema,
});

const getModuleConfigsContract = oc
  .output(z.array(organizationModuleConfigSchema))
  .route({
    method: "GET",
    summary: "List module configurations",
    description:
      "Returns every product module configured for the organization, with its enabled flag and settings.",
  });

const getModuleConfigContract = oc
  .input(z.object({ module: productModuleSchema }))
  .output(organizationModuleConfigSchema)
  .route({
    method: "GET",
    summary: "Get a module configuration",
    description:
      "Returns the enabled flag and settings for one product module in the organization.",
  });

const createOrUpdateModuleConfigContract = oc
  .input(createOrUpdateOrganizationModuleConfigInputSchema)
  .output(organizationModuleConfigSchema)
  .route({
    summary: "Save module configuration",
    description:
      "Creates or updates a product module's enabled flag and settings, merging edits against the base snapshot under a lock. Certain setting changes reset agreement estimates and forecasts.",
  });

export const orgConfigModulesContract = {
  getModuleConfigs: getModuleConfigsContract,
  getModuleConfig: getModuleConfigContract,
  createOrUpdateModuleConfig: createOrUpdateModuleConfigContract,
};

export type OrgConfigModulesContract = typeof orgConfigModulesContract;
